
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { Article } from '@api/api';
import { ShareArticle } from '@components/article/ShareArticle';
import { ShareContainer } from '@components/collection/CollectionInfo/CollectionInfo.styles';
import { ArticleCard } from '@components/common/Card/ArticleCard';
import { DataFeed } from '@components/common/DataFeed';
import {
  Container,
  Header,
  HeaderDescription,
  HeaderName,
  HeaderSubname,
} from '@components/common/DataFeed/DataFeed.styles';
import { Seo } from '@components/common/Seo';
import { AuthorJsonLd } from '@components/common/Seo/JsonLd/AuthorJsonLd';
import LinkedInIcon from '@components/icons/linkedinIconDark.svg';
import MailIcon from '@components/icons/mailIconDark.svg';
import TwitterIcon from '@components/icons/twitterIconDark.svg';
import {
  AuthorMedia,
  DataContainerHead,
  DataContainerTitle,
  HeaderContainer,
  HeaderContent,
  HeaderImage,
} from '@components/writer/WriterPage.styles';
import { useCurrentPaginationPage, usePagination } from '@hooks/usePagination';
import { fetchWriter, useWriter } from '@queries/useWriter';
import { fetchWriterArticles, useWriterArticles } from '@queries/useWriterArticles';
import { buildArticleLink, buildAuthorPageLink } from '@utils/link';
import { noop } from '@utils/noop';
import { withGlobalSettings } from '@utils/pageFetchEnhancer';
import { buildOpenGraph, buildResourceSeoSettings } from '@utils/seo';
import { GetServerSideProps } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { dehydrate } from 'react-query';
import { PageProps } from '../../../types/page';

type AuthorLinkProps = {
  href: string;
  icon: React.ReactNode;
};

const AuthorLink = ({ href, icon }: AuthorLinkProps) => {
  if (!href) return null;
  return (
    <AuthorMedia href={href} target="_blank" rel="noreferrer">
      {icon}
    </AuthorMedia>
  );
};

export default function AuthorPage({ seo }): ReactElement {
  const { query } = useRouter();
  const { id } = query;
  const { t } = useTranslation('common');

  const { data } = useWriter(String(id));

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const avatarUrl = data.avatar.url;

  const url = buildAuthorPageLink(data.id, data.slug);

  const currentPage = useCurrentPaginationPage();

  const { data: articles, totalPages } = useWriterArticles(String(data.id), currentPage);

  const { onPageChange } = usePagination(currentPage, totalPages);

  const { contactEmail, twitter, linkedin } = data;

  return (
    <>
      <Seo
        canonical={url}
        siteName={seo.siteName}
        metaTitle={data.name}
        metaDescription={data.bio}
        openGraph={buildOpenGraph({
          url,
          type: 'website',
          sitename: seo.siteName,
          description: data.bio,
          title: data.name,
        })}
      />
      <AuthorJsonLd author={data} publisherName="Coinpaper" publisherUrl="https://coinpaper.com" />
      <Container>
        <Header className="header-container">
          <HeaderContainer>
            <HeaderContent>
              <div className="head-box">
                <HeaderName>Authors /</HeaderName>
                <div className="header-subname">
                  <HeaderSubname as="h1" className="subname">
                    {data.name}
                  </HeaderSubname>

                  <HeaderImage className="mobile">
                    <img src={avatarUrl} alt="Avatar" title="Avatar" />
                  </HeaderImage>
                </div>

                {data.bio && <HeaderDescription>{data.bio}</HeaderDescription>}
              </div>

              <div className="share-box">
                <ShareContainer className="share-container">
                  <AuthorLink href={twitter} icon={<TwitterIcon />} />
                  <AuthorLink href={`mailto:${contactEmail}`} icon={<MailIcon />} />
                  <AuthorLink href={linkedin} icon={<LinkedInIcon />} />
                  <ShareArticle
                    articleLink={buildAuthorPageLink(data.id, data.slug)}
                    linkAsIcon
                    // TODO: implement share logic
                    onClickLink={noop}
                    socialLink={false}
                  />
                </ShareContainer>
              </div>
            </HeaderContent>
            <HeaderImage className="desktop">
              <img src={avatarUrl} alt="Avatar" title="Avatar" />
            </HeaderImage>
          </HeaderContainer>
        </Header>

        <DataContainerHead>
          <DataContainerTitle>
            <h2 className="head">Discover articles prepared by {data.name}:</h2>
          </DataContainerTitle>
        </DataContainerHead>

        {(articles as { items?: Article[] })?.items && (
          <DataFeed<Article>
            data={(articles as { items?: Article[] })?.items}
            hideHeader
            loading={false}
            loadMoreText={t('load-more')}
            renderData={(article) => (
              <ArticleCard
                key={article.id}
                article={article}
                href={buildArticleLink(article.id, article.slug)}
                variant="medium"
                thumbnail="left"
                size="large"
              />
            )}
            variant="list"
            onPageChange={onPageChange}
            totalPages={totalPages}
            activePage={currentPage}
          />
        )}
      </Container>
    </>
  );
}

 const _getServerSideProps: GetServerSideProps = withGlobalSettings<PageProps>(
  async (nextContext, { queryClient, globalSettingsPromise, adScriptsPromise }) => {
    const { id } = nextContext.params;

    try {
      const [, globalSettings, adScripts] = await Promise.all([
        queryClient.fetchQuery(['writer', id], () => fetchWriter(String(id))),
        globalSettingsPromise,
        adScriptsPromise,
        queryClient.prefetchInfiniteQuery(['writer-articles', id], () =>
          fetchWriterArticles(String(id), 0),
        ),
      ]);

      const seo = buildResourceSeoSettings(
        {
          ...globalSettings.global.seo,
          siteName: globalSettings.global.siteName,
        },
        { id: '' },
      );

      return {
        props: {
          dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
          seo,
          adScripts,
          globalSettings,
        },
      };
    } catch (error) {
      return {
        notFound: true,
      };
    }
  },
);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/authors/[id]/[slug]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  